import { graphql, useStaticQuery } from "gatsby";
import { useI18next } from "gatsby-plugin-react-i18next";
import Helmet from "react-helmet";
import IconImage from "../assets/images/icon.webp";

type ReviewData = {
  ratingValue: number;
  ratingCount: number;
  bestRating?: number;
  worstRating?: number;
  reviewCount?: number;
};

type AddressData = {
  streetAddress: string;
  addressLocality: string; // city
  addressRegion?: string; // state or region
  postalCode: string;
  addressCountry: string;
};

type SeoProps = {
  description: string;
  lang: string;
  meta: [];
  title: string;
  reviewData?: ReviewData;
  businessName?: string;
  businessUrl?: string;
  address?: AddressData;
  customCanonicalPath?: string; // Optional custom path for canonical URL
};

const Seo = ({
  description,
  lang,
  meta,
  title,
  reviewData,
  businessName,
  businessUrl,
  address,
  customCanonicalPath,
}: SeoProps) => {
  // Get i18n information for canonical URL
  const { language, originalPath } = useI18next();

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            keywords
            image
            siteUrl
          }
        }
      }
    `
  );

  const image = "https://www.lanchaslagaviota.com" + IconImage;
  const keywords = site.siteMetadata.keywords;
  const metaDescription = description || site.siteMetadata.description;
  const titleTemplate = title || site.siteMetadata?.title;
  const siteUrl = site.siteMetadata.siteUrl || "https://www.lanchaslagaviota.com";

  // Self-referential canonical URL based on current language
  const canonicalPath = customCanonicalPath || (language === "en" ? originalPath : `/${language}${originalPath}`);
  const canonicalUrl = `${siteUrl}${canonicalPath}`;

  // Generate JSON-LD schema markup for reviews
  const generateReviewSchema = () => {
    if (!reviewData) return null;

    const company = businessName || site.siteMetadata?.title || "";
    const url = businessUrl || "";

    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: company,
      url: url,
      address: address
        ? {
            "@type": "PostalAddress",
            ...address,
          }
        : undefined,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: reviewData.ratingValue,
        ratingCount: reviewData.ratingCount,
        bestRating: reviewData.bestRating || 5,
        worstRating: reviewData.worstRating || 1,
        reviewCount: reviewData.reviewCount || reviewData.ratingCount,
      },
    };
  };

  const reviewSchema = reviewData ? generateReviewSchema() : null;

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={titleTemplate ? `%s | ${titleTemplate}` : undefined}
      link={[
        {
          rel: "canonical",
          href: canonicalUrl,
        },
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `keywords`,
          content: keywords,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:image`,
          content: image,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:image`,
          content: image,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ].concat(meta)}
    >
      <script async src="//www.instagram.com/embed.js"></script>

      {/* ContentSquare tracking script */}
      <script type="text/javascript">{`
        (function (c, s, q, u, a, r, e) {
            c.hj=c.hj||function(){(c.hj.q=c.hj.q||[]).push(arguments)};
            c._hjSettings = { hjid: a };
            r = s.getElementsByTagName('head')[0];
            e = s.createElement('script');
            e.async = true;
            e.src = q + c._hjSettings.hjid + u;
            r.appendChild(e);
        })(window, document, 'https://static.hj.contentsquare.net/c/csq-', '.js', 5362767);
      `}</script>

      {reviewSchema && <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>}
    </Helmet>
  );
};

export default Seo;
